import { Badge, Table, TableColumn } from '@ibsys/kit';
import { IconButton, TruncatedText } from '@/shared/ui';
import { PencilIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IntegrationListItem } from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';

type IntegrationsTableProps = {
  items: IntegrationListItem[];
  total: number;
  page: number;
  perPage: number;
  onChangePagination: (page: number, perPage: number) => void;
  onEdit: (integration: IntegrationListItem) => void;
};

export const IntegrationsTable: FC<IntegrationsTableProps> = ({
  items,
  total,
  page,
  perPage,
  onChangePagination,
  onEdit,
}) => {
  const { t } = useTranslation(['integrations', 'common']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');

  const columns: TableColumn<IntegrationListItem>[] = useMemo(() => {
    const base: TableColumn<IntegrationListItem>[] = [
      {
        accessorKey: 'code',
        header: () => <span>{t('integrations:table.code')}</span>,
        size: 200,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.code}</span>,
      },
      {
        accessorKey: 'name',
        header: () => <span>{t('integrations:table.name')}</span>,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'isActive',
        header: () => <span>{t('integrations:table.isActive')}</span>,
        size: 140,
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'primary' : 'secondary'}>
            {row.original.isActive ? t('integrations:detail.active') : t('integrations:detail.inactive')}
          </Badge>
        ),
      },
      {
        accessorKey: 'description',
        header: () => <span>{t('integrations:table.description')}</span>,
        cell: ({ row }) => (
          <TruncatedText lines={2} className="text-sm text-muted-foreground">
            {row.original.description ?? ''}
          </TruncatedText>
        ),
      },
    ];

    if (canAdmin) {
      base.push({
        accessorKey: 'code',
        id: 'actions',
        header: () => <span className="sr-only">{t('integrations:table.actions')}</span>,
        size: 80,
        cell: ({ row }) => (
          <IconButton
            variant="ghost"
            size="icon"
            label={t('integrations:table.edit')}
            icon={<PencilIcon className="size-4" />}
            onClick={() => onEdit(row.original)}
          />
        ),
      });
    }

    return base;
  }, [t, canAdmin, onEdit]);

  return (
    <Table
      data={items}
      columns={columns}
      getKey={(row) => row.code}
      pagination={{ total, page, perPage }}
      type="paginated"
      onChangePagination={onChangePagination}
      styles={{ size: 'sm' }}
    />
  );
};
